import React, { useEffect, useMemo, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, Link } from 'react-router-dom';
import { gql, useMutation, useQuery } from '@apollo/client';
import moment from 'moment';
import { hasPermissionAixcDeploy } from 'shared/lib/hasPermission';
import { FFAI_PATH_PREFIX } from 'shared/lib/ffaiSite';
import { pageRequestBlocker } from 'shared/lib/pageRequests';
import { flashMessage } from '~/app/actions';
import { getStudioEditorUrl } from '~/lib/studioUrl';
import { pagePublicPath } from '~/lib/pagePublicPath';
import { pageStatus } from '~/lib/pageStatus';
import { S, A, DARK } from '~/app/ui/tokens';
import { tr } from '~/app/ui/lang';
import Button from '~/app/ui/Button';
import { Page, PageHeader, Toolbar, Spacer } from '~/app/ui/Page';
import SearchField from '~/app/ui/SearchField';
import Segmented from '~/app/ui/Segmented';
import StatusBadge from '~/app/ui/StatusBadge';
import EmptyState from '~/app/ui/EmptyState';
import Pager from '~/app/ui/Pager';
import ConfirmDialog from '~/app/ui/ConfirmDialog';
import { RowMenu, MenuItem } from '~/app/ui/Menu';
import { Table, SkeletonRows, Checkbox, BulkBar, rowClick, useRowClasses } from '~/app/ui/Table';
import { useQueryParams, remembered } from '~/app/ui/useQueryParams';
import RedeploySelected from './RedeploySelected';

export const PAGES_QUERY = gql`
  query getPages($pageIndex: Int!, $searchQuery: String, $pageSize: Int, $pathPrefix: String, $sort: String) {
    pagesPaginated(pageIndex: $pageIndex, searchQuery: $searchQuery, pageSize: $pageSize, pathPrefix: $pathPrefix, sort: $sort) {
      _id
      editorType
      title
      is_root
      path
      updatedAt
      createdAt
      updatedBy
      canUserEdit
      canUserApprove
      canUserCopy
      canUserRemove
      canUserDelete
      canUserRevert
      canUserRequestApproval
      state
      publishedVersion
      category {
        title
      }
      versions {
        _id
        versionId
      }
    }
    pagesPaginatedCount(searchQuery: $searchQuery, pathPrefix: $pathPrefix)
  }
`;

const SITE_COUNTS = gql`
  query getPageSiteCounts($searchQuery: String) {
    ff: pagesPaginatedCount(searchQuery: $searchQuery, pathPrefix: "")
    aixc: pagesPaginatedCount(searchQuery: $searchQuery, pathPrefix: "/cxc10")
    ffai: pagesPaginatedCount(searchQuery: $searchQuery, pathPrefix: "${FFAI_PATH_PREFIX}")
  }
`;

const UPDATE_STATE = gql`
  mutation updatePageState($id: String!, $state: String) {
    updatePageState(_id: $id, state: $state) {
      _id
    }
  }
`;

type Version = { _id: string; versionId: string };
export type ListPage = {
  _id: string;
  editorType?: string | null;
  title: string;
  is_root?: boolean;
  path: string;
  updatedAt?: string | null;
  createdAt?: string | null;
  updatedBy?: string | null;
  canUserEdit?: boolean;
  canUserCopy?: boolean;
  canUserRemove?: boolean;
  canUserDelete?: boolean;
  state?: string | null;
  publishedVersion?: string | null;
  category?: { title?: string | null } | null;
  versions: Version[];
};

type Site = 'ff' | 'aixc' | 'ffai';
const SITES: Array<{ value: Site; label: string; prefix: string }> = [
  { value: 'ff', label: 'ff.com', prefix: '' },
  { value: 'aixc', label: 'AIXC', prefix: '/cxc10' },
  { value: 'ffai', label: 'FFAI', prefix: FFAI_PATH_PREFIX }
];
const SORTS = [
  { value: 'updated', label: 'Recently updated' },
  { value: 'created', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'title', label: 'Title A–Z' }
];
const SIZES = [10, 25, 50, 100];
const MAX_PAGE_INDEX = 100000;
const SIZE_KEY = 'genesis_page_size';
const SORT_KEY = 'gx-pages-sort';

type Dialog = { kind: 'delete' | 'withdraw'; page: ListPage } | null;

const useStyles = createUseStyles({
  sort: {
    height: 36,
    padding: '0 30px 0 10px',
    border: `1px solid ${S.fieldLine}`,
    borderRadius: A.radius,
    background: `${A.surface} url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6'%3E%3Cpath d='M0 0l5 6 5-6z' fill='%236B6D72'/%3E%3C/svg%3E") no-repeat right 11px center`,
    appearance: 'none',
    fontFamily: S.ui,
    fontSize: 14,
    color: S.fg,
    cursor: 'pointer',
    '&:hover': { borderColor: '#B4BAC2' },
    '&:focus-visible': A.focus,
    [DARK]: { '&:hover': { borderColor: S.muted } }
  },
  busy: { opacity: 0.55, transition: 'opacity 120ms ease' },
  colCheck: { width: 44, paddingRight: '0 !important' },
  colStatus: { width: 170, '@media (max-width: 640px)': { display: 'none' } },
  // On a phone the status sits under the title instead of in its own column.
  inlineStatus: { display: 'none', marginTop: 6, '@media (max-width: 640px)': { display: 'block' } },
  colCategory: { width: 150, '@media (max-width: 1100px)': { display: 'none' } },
  colUpdated: { width: 190, '@media (max-width: 760px)': { display: 'none' } },
  colMenu: { width: 52, textAlign: 'right' },
  title: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontWeight: 600,
    color: S.fg,
    textDecoration: 'none',
    '&:hover': { textDecoration: 'underline', textUnderlineOffset: 3 },
    '&:focus-visible': A.focus,
    '& .material-icons': { fontSize: 15, color: S.muted }
  },
  path: {
    marginTop: 3,
    fontFamily: S.mono,
    fontSize: 12,
    color: S.muted,
    wordBreak: 'break-all'
  },
  studio: {
    display: 'inline-block',
    marginLeft: 8,
    padding: '1px 6px',
    border: `1px solid ${S.fieldLine}`,
    borderRadius: 4,
    fontFamily: S.label,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: S.muted,
    verticalAlign: 1
  },
  muted: { color: S.muted, fontSize: 13 },
  when: { fontSize: 14, whiteSpace: 'nowrap' },
  who: { fontSize: 12, color: S.muted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 },
  barButton: {
    all: 'unset',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    height: 32,
    padding: '0 12px',
    borderRadius: 8,
    background: S.onFg,
    color: S.fg,
    fontWeight: 600,
    fontSize: 14,
    cursor: 'pointer',
    '& .material-icons': { fontSize: 18 },
    '&:hover': { background: '#E9EBEE' },
    '&:focus-visible': { outline: `2px solid ${S.onFg}`, outlineOffset: 2 },
    [DARK]: { '&:hover': { background: S.chromeHover } }
  },
  link: { all: 'unset', color: S.fg, fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer', '&:focus-visible': A.focus }
});

const toTime = (value?: string | null) => {
  if (!value) return null;
  const n = Number(value);
  const m = Number.isNaN(n) ? moment(value) : moment(n);
  return m.isValid() ? m : null;
};

/** "3 hours ago" for the last week, then the date. */
const whenLabel = (m: moment.Moment) => (moment().diff(m, 'days') < 7 ? m.fromNow() : m.format(m.year() === moment().year() ? 'MMM D' : 'MMM D, YYYY'));

const activeVersionId = (page: ListPage) => page.versions.find(v => v.versionId === page.publishedVersion)?._id || null;

const PagesScreen: React.FC = () => {
  const c = useStyles();
  const rowCls = useRowClasses();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<{ pathPrefix?: string; searchQuery?: string } | undefined>();
  const [params, setParams] = useQueryParams();
  const user = useSelector((state: { app: { user: { isSuperAdmin?: boolean; isActive?: boolean } } }) => state.app.user) || {};
  const isSuperAdmin = !!user.isSuperAdmin;
  const isAixcDeployer = hasPermissionAixcDeploy(user) && !isSuperAdmin;
  const canManageDeploy = isSuperAdmin || isAixcDeployer;

  // The view lives in the address. (Older links kept the site in history state; still honored.)
  const legacyPrefix = location.state?.pathPrefix;
  const siteParam = params.get('site') as Site | null;
  const site: Site = isAixcDeployer ? 'aixc'
    : SITES.some(s => s.value === siteParam) ? (siteParam as Site)
      : (SITES.find(s => s.prefix && s.prefix === legacyPrefix)?.value || 'ff');
  const siteInfo = SITES.find(s => s.value === site) || SITES[0];
  const pathPrefix = siteInfo.prefix;
  const q = params.get('q') ?? location.state?.searchQuery ?? '';
  const sizeParam = Number(params.get('size'));
  const pageSize = SIZES.includes(sizeParam) ? sizeParam : (SIZES.includes(Number(remembered.get(SIZE_KEY))) ? Number(remembered.get(SIZE_KEY)) : 25);
  const isSort = (v: string | null): v is string => SORTS.some(s => s.value === v);
  const sortParam = params.get('sort');
  const sortSaved = remembered.get(SORT_KEY);
  const sort = isSort(sortParam) ? sortParam : (isSort(sortSaved) ? sortSaved : 'updated');
  // Capped so a mangled link (?page=1e400) can't overflow the query's Int; the effect below then moves to the last page.
  const pageIndex = Math.min(MAX_PAGE_INDEX, Math.max(1, Math.floor(Number(params.get('page')) || 1)));

  const { data, previousData, loading, error, refetch } = useQuery(PAGES_QUERY, {
    variables: { pageIndex, searchQuery: q, pageSize, pathPrefix, sort },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true
  });
  const counts = useQuery(SITE_COUNTS, { variables: { searchQuery: q }, skip: isAixcDeployer, fetchPolicy: 'cache-and-network' });
  const shown = data || previousData;
  const pages: ListPage[] = useMemo(() => shown?.pagesPaginated || [], [shown]);
  const total: number = shown?.pagesPaginatedCount || 0;

  // A page number past the end (after deleting, or an old link): go to the last page.
  const lastPage = Math.max(1, Math.ceil(total / pageSize));
  const pastEnd = pageIndex > lastPage;
  useEffect(() => {
    if (!data || loading) return;
    if (pastEnd) setParams({ page: lastPage === 1 ? null : lastPage }, { replace: true });
  }, [data, loading, pastEnd, lastPage]); // eslint-disable-line react-hooks/exhaustive-deps

  const [selected, setSelected] = useState<Record<string, string>>({});
  const [dialog, setDialog] = useState<Dialog>(null);
  const [redeploying, setRedeploying] = useState(false);
  // The selection belongs to the rows on screen.
  useEffect(() => { setSelected({}); }, [site, q, pageIndex, pageSize, sort]);

  const [updateState] = useMutation(UPDATE_STATE, { refetchQueries: ['getPages', 'getPageSiteCounts', 'getPagesPendingApproval', 'getNavData'] });

  const selectable = useMemo(() => pages.filter(p => p.editorType !== 'studio' && activeVersionId(p)), [pages]);
  const selectedIds = Object.keys(selected);
  const allSelected = selectable.length > 0 && selectable.every(p => selected[p._id]);
  const toggleAll = () => setSelected(allSelected ? {} : selectable.reduce<Record<string, string>>((all, p) => ({ ...all, [p._id]: activeVersionId(p) as string }), {}));
  const toggle = (p: ListPage) => setSelected((cur) => {
    const next = { ...cur };
    if (next[p._id]) delete next[p._id];
    else next[p._id] = activeVersionId(p) as string;
    return next;
  });

  const openPage = (p: ListPage, newTab: boolean) => {
    // Studio opens only for people who may edit the page, as the old list's Edit link did.
    if (p.editorType === 'studio' && p.canUserEdit) { window.open(getStudioEditorUrl(p._id), '_blank', 'noopener'); return; }
    const to = p.canUserEdit ? `/admin/page/${p._id}` : `/admin/page/versions/${p._id}`;
    if (newTab) window.open(to, '_blank', 'noopener');
    else history.push(to);
  };

  // Shown but unavailable, with the reason, while something else for the page is waiting.
  const requestItem = (p: ListPage, kind: 'delete' | 'withdraw', item: MenuItem): MenuItem => {
    const blocker = pageRequestBlocker(p, kind);
    return blocker ? { ...item, disabled: true, hint: blocker } : item;
  };

  const menuFor = (p: ListPage): MenuItem[][] => {
    const studio = p.editorType === 'studio';
    return [
      [
        ...(studio && p.canUserEdit ? [{ key: 'studio', label: 'Open in Studio', icon: 'open_in_new', href: getStudioEditorUrl(p._id), newTab: true }] : []),
        ...(!studio && p.canUserEdit ? [{ key: 'edit', label: 'Open in editor', icon: 'edit', to: `/admin/page/${p._id}` }] : []),
        ...(!studio ? [{ key: 'preview', label: 'Preview draft', icon: 'visibility', hint: 'Opens in a new tab', href: `${p.path}?preview=1&v=DRAFT`, newTab: true }] : []),
        ...(!studio ? [{ key: 'analytics', label: 'Analytics', icon: 'insights', to: `/admin/page/${p._id}/analytics` }] : []),
        ...(p.versions.length > 0 ? [{ key: 'versions', label: 'Version history', icon: 'history', to: `/admin/page/versions/${p._id}` }] : []),
        ...(studio && p.canUserEdit ? [{ key: 'meta', label: 'SEO & sharing', icon: 'travel_explore', to: `/admin/page/${p._id}/edit-meta` }] : [])
      ],
      p.canUserCopy ? [{ key: 'copy', label: 'Make a copy', icon: 'content_copy', to: `/admin/page/copy/${p._id}` }] : [],
      [
        ...(p.canUserRemove && p.publishedVersion && p.state !== 'PENDING_REMOVAL'
          ? [requestItem(p, 'withdraw', { key: 'withdraw', label: 'Withdraw from site…', icon: 'visibility_off', danger: true, onSelect: () => setDialog({ kind: 'withdraw', page: p }) })] : []),
        ...(p.canUserDelete && p.state !== 'PENDING_DELETE'
          ? [requestItem(p, 'delete', { key: 'delete', label: 'Request deletion…', icon: 'delete_outline', danger: true, onSelect: () => setDialog({ kind: 'delete', page: p }) })] : [])
      ]
    ];
  };

  const confirmDialog = async () => {
    if (!dialog) return;
    const { kind, page } = dialog;
    await updateState({ variables: { id: page._id, state: kind === 'delete' ? 'PENDING_DELETE' : 'PENDING_REMOVAL' } });
    dispatch(flashMessage(kind === 'delete'
      ? `Deletion of “${page.title}” requested. An approver needs to confirm it.`
      : `Withdrawal of “${page.title}” requested. An approver needs to confirm it.`));
  };

  const siteLabel = siteInfo.label;
  const otherMatches = !isAixcDeployer && q && counts.data
    ? SITES.filter(s => s.value !== site && counts.data[s.value] > 0).map(s => ({ ...s, n: counts.data[s.value] as number }))
    : [];
  const columns = canManageDeploy ? 6 : 5;
  // Past the end the list is empty only until the effect above moves to the last page.
  const initialLoading = (loading && !shown) || (pastEnd && total > 0 && pages.length === 0);

  let body: React.ReactNode;
  if (error && !shown) {
    body = (
      <tr><td colSpan={columns}>
        <EmptyState tone="error" title="Couldn’t load pages" action={<Button onClick={() => refetch()} icon="refresh">Try again</Button>}>
          {error.message}
        </EmptyState>
      </td></tr>
    );
  } else if (initialLoading) {
    body = <SkeletonRows rows={Math.min(pageSize, 10)} widths={[...(canManageDeploy ? [16] : []), '60%', 90, '70%', '60%', 0]} />;
  } else if (pages.length === 0) {
    body = (
      <tr><td colSpan={columns}>
        {q ? (
          <EmptyState icon="search_off" title={`No ${siteLabel} pages match “${q}”`} action={<Button onClick={() => setParams({ q: null, page: null })}>Clear search</Button>}>
            Search looks at page titles and paths.
            {otherMatches.length > 0 && (
              <>
                {' '}Found on{' '}
                {otherMatches.map((s, i) => (
                  <React.Fragment key={s.value}>
                    {i > 0 && ' and '}
                    <button type="button" className={c.link} onClick={() => setParams({ site: s.value, page: null })}>{s.label} ({s.n})</button>
                  </React.Fragment>
                ))}
                .
              </>
            )}
          </EmptyState>
        ) : (
          <EmptyState icon="description" title={`No ${siteLabel} pages yet`} action={<Button variant="primary" icon="add" to="/admin/page/new">New page</Button>} />
        )}
      </td></tr>
    );
  } else {
    body = pages.map((p) => {
      const status = pageStatus(p);
      const studio = p.editorType === 'studio';
      const updated = toTime(p.updatedAt);
      const versionId = activeVersionId(p);
      const opensStudio = studio && p.canUserEdit;
      const href = opensStudio ? getStudioEditorUrl(p._id) : (p.canUserEdit ? `/admin/page/${p._id}` : `/admin/page/versions/${p._id}`);
      return (
        <tr key={p._id} className={[rowCls.clickable, selected[p._id] ? rowCls.selected : ''].join(' ')} {...rowClick(newTab => openPage(p, newTab), !!selected[p._id])} data-page-row={p._id}>
          {canManageDeploy && (
            // A click that just misses the checkbox shouldn't open the page.
            <td className={c.colCheck} data-no-row-click>
              {!studio && (
                <Checkbox
                  checked={!!selected[p._id]}
                  disabled={!versionId}
                  onChange={() => toggle(p)}
                  label={`Select ${p.title}`}
                  title={versionId ? `Select ${p.title} to redeploy` : 'Not live, so there’s nothing to redeploy'}
                />
              )}
            </td>
          )}
          <td>
            {opensStudio
              ? <a className={c.title} href={href} target="_blank" rel="noopener noreferrer">{p.title}<i className="material-icons" aria-hidden>open_in_new</i></a>
              : <Link className={c.title} to={href}>{p.title}</Link>}
            {studio && <span className={c.studio} title={opensStudio ? 'Built in Studio; opens in Studio' : 'Built in Studio'}>Studio</span>}
            <div className={c.path}>{pagePublicPath(p.path)}</div>
            <div className={c.inlineStatus}><StatusBadge tone={status.tone} label={status.label} title={status.hint} /></div>
          </td>
          <td className={c.colStatus}><StatusBadge tone={status.tone} label={status.label} title={status.hint} /></td>
          <td className={`${c.colCategory} ${c.muted}`}>{p.is_root ? 'Root' : (p.category?.title || 'Uncategorized').replace(/cxc10/gi, 'AIXC')}</td>
          <td className={c.colUpdated}>
            {updated ? <div className={c.when} title={updated.format('MMM D, YYYY h:mm A')}>{whenLabel(updated)}</div> : <span className={c.muted}>—</span>}
            {p.updatedBy && <div className={c.who}>by {p.updatedBy}</div>}
          </td>
          <td className={c.colMenu} data-no-row-click>
            <RowMenu label={`Actions for ${p.title}`} groups={menuFor(p)} testId={`page-menu-${p._id}`} />
          </td>
        </tr>
      );
    });
  }

  const siteOptions = SITES.map(s => ({ value: s.value, label: s.label, count: counts.data ? counts.data[s.value] : null, testId: `site-${s.value}` }));

  return (
    <Page testId="pages-screen">
      <PageHeader
        title={tr('Pages', '页面')}
        description={isAixcDeployer ? 'AIXC pages you can edit and deploy.' : 'Every page on ff.com, AIXC and FFAI. Open one to edit it.'}
        actions={<Button variant="primary" icon="add" to="/admin/page/new" data-testid="new-page">New page</Button>}
      />
      <Toolbar>
        {!isAixcDeployer && (
          <Segmented label="Site" value={site} options={siteOptions} onChange={v => setParams({ site: v === 'ff' ? null : v, page: null })} />
        )}
        <SearchField
          value={q}
          onChange={v => setParams({ q: v, page: null }, { replace: true })}
          placeholder="Search titles and paths"
          testId="pages-search"
        />
        <Spacer />
        <label>
          <span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Sort</span>
          <select className={c.sort} value={sort} onChange={(e) => { remembered.set(SORT_KEY, e.target.value); setParams({ sort: e.target.value, page: null }); }} data-testid="pages-sort">
            {SORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </label>
      </Toolbar>

      <div className={loading && shown ? c.busy : undefined} aria-busy={loading || undefined}>
        <Table label={`${siteLabel} pages`} testId="pages-table">
          <thead>
            <tr>
              {canManageDeploy && (
                <th className={c.colCheck}>
                  <Checkbox
                    checked={allSelected}
                    indeterminate={selectedIds.length > 0}
                    disabled={selectable.length === 0}
                    onChange={toggleAll}
                    label="Select all live pages on this page of the list"
                  />
                </th>
              )}
              <th>Page</th>
              <th className={c.colStatus}>Status</th>
              <th className={c.colCategory}>Category</th>
              <th className={c.colUpdated}>Updated</th>
              <th className={c.colMenu}><span style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' }}>Actions</span></th>
            </tr>
          </thead>
          <tbody>{body}</tbody>
        </Table>
      </div>

      {total > 0 && (
        <Pager
          page={Math.min(pageIndex, lastPage)}
          pageSize={pageSize}
          total={total}
          noun="pages"
          sizes={SIZES}
          onPage={n => setParams({ page: n === 1 ? null : n })}
          onPageSize={(n) => { remembered.set(SIZE_KEY, String(n)); setParams({ size: n, page: null }); }}
        />
      )}

      {/* Hidden while the redeploy dialog is open: clearing the selection behind it would empty its list. */}
      {canManageDeploy && !redeploying && (
        <BulkBar count={selectedIds.length} noun={selectedIds.length === 1 ? 'page' : 'pages'} onClear={() => setSelected({})}>
          <button type="button" className={c.barButton} onClick={() => setRedeploying(true)} data-testid="bulk-redeploy">
            <i className="material-icons" aria-hidden>replay</i>
            Redeploy…
          </button>
        </BulkBar>
      )}

      {redeploying && (
        <RedeploySelected
          pages={pages.filter(p => selected[p._id])}
          selectedVersionIds={Object.values(selected)}
          onCancel={() => setRedeploying(false)}
          onDone={() => { setRedeploying(false); setSelected({}); refetch(); }}
        />
      )}

      {dialog && (
        <ConfirmDialog
          danger
          title={dialog.kind === 'delete' ? `Request deletion of “${dialog.page.title}”?` : `Withdraw “${dialog.page.title}” from the site?`}
          confirmLabel={dialog.kind === 'delete' ? 'Request deletion' : 'Request withdrawal'}
          onConfirm={confirmDialog}
          onClose={() => setDialog(null)}
          testId="page-action-dialog"
        >
          {dialog.kind === 'delete' ? (
            <p>
              An approver has to confirm. Then the page is deleted from Genesis
              {dialog.page.publishedVersion ? ' and comes off the site with the next deployment' : ''}.
              Until then the page stays as it is.
            </p>
          ) : (
            <p>An approver has to confirm, then the page comes off the site with the next deployment. The page and its versions stay in Genesis, so it can be published again.</p>
          )}
          <p style={{ fontFamily: S.mono, fontSize: 12, color: S.muted }}>{pagePublicPath(dialog.page.path)}</p>
        </ConfirmDialog>
      )}
    </Page>
  );
};

export default PagesScreen;
